import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { InsuranceCarrier } from '@/types/clinic';
import { InsuranceCard } from '@/components/InsuranceCard';
import { ProgressBar } from '@/components/ProgressBar';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { Footer } from '@/components/Footer';
import { useClinicConfigContext } from '@/contexts/ClinicConfigContext';

export function InsurancePage() {
  const { clinicSlug } = useParams<{ clinicSlug: string }>();
  const navigate = useNavigate();
  const { config, configLoading, configError } = useClinicConfigContext();
  const [selectedInsurance, setSelectedInsurance] =
    useState<InsuranceCarrier | null>(null);
  const [isNavigating, setIsNavigating] = useState(false);

  // Theme is initialized globally in App via ThemeInitializer

  const handleNext = async () => {
    if (selectedInsurance && !isNavigating) {
      setIsNavigating(true);
      try {
        // Store selected insurance in cookie
        document.cookie = `selected_insurance_id=${encodeURIComponent(
          selectedInsurance.id
        )}; path=/; max-age=604800`;

        // Navigate to estimated balance page
        navigate(`/${clinicSlug}/estimated-balance`);
      } catch (error) {
        console.error('Navigation error:', error);
        setIsNavigating(false);
      }
    }
  };

  if (configLoading) {
    return (
      <div className='min-h-screen relative'>
        <LoadingSpinner fullScreen size='lg' text='Loading...' />
      </div>
    );
  }

  if (configError || !config) {
    return (
      <div className='min-h-screen relative flex items-center justify-center'>
        <div className='text-center'>
          <h1 className='text-4xl font-bold text-gray-800 mb-4'>404</h1>
          <p className='text-xl text-gray-600 mb-8'>Clinic not found</p>
        </div>
      </div>
    );
  }

  return (
    <div className='h-screen w-screen overflow-hidden relative flex flex-col'>
      {/* Header with Logo */}
      <div className='flex-shrink-0 p-6'>
        <div className='flex items-center'>
          <div className='w-fit'>
            <img
              src={'/logo.png'}
              alt={`${config.location.name} logo`}
              className='w-full h-full object-contain'
            />
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className='flex-1 flex flex-col justify-center px-6 py-8'>
        <div className='max-w-4xl mx-auto w-full'>
          {/* Progress Bar and Navigation */}
          <div className='flex flex-row justify-center items-center gap-4'>
            <div className='flex items-center justify-between'>
              <button
                onClick={() => navigate(`/${clinicSlug}/patient-info`)}
                className='text-gray-600 hover:text-gray-800 flex items-center space-x-2'
              >
                <svg
                  className='w-4 h-4'
                  fill='none'
                  stroke='var(--clinic-icon-stroke)'
                  viewBox='0 0 24 24'
                >
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={2}
                    d='M15 19l-7-7 7-7'
                  />
                </svg>
                <span>Prev</span>
              </button>
            </div>

            {/* Progress Bar */}
            <ProgressBar progress={90} animated={false} />
          </div>

          {/* Title */}
          <div className='text-center mb-12 mt-8 px-8'>
            <h2 className='text-xl lg:text-3xl font-semibold text-gray-900'>
              Select your insurance
            </h2>
          </div>

          {/* Insurance Provider Cards */}
          <div className='grid md:grid-cols-2 lg:grid-cols-2 gap-6 mb-8 px-8'>
            {config.booking_settings.insurance_carriers_data.map(insurance => (
              <InsuranceCard
                key={insurance.id}
                insurance={{
                  id: insurance.id.toString(),
                  name: insurance.name,
                  logo: insurance.name.charAt(0).toUpperCase(),
                  logoColor: '#3B82F6',
                  backgroundColor: '#EFF6FF',
                }}
                isSelected={selectedInsurance?.id === insurance.id}
                onClick={() => setSelectedInsurance(insurance)}
                primaryColor={'var(--clinic-primary)'}
              />
            ))}
          </div>

          {/* Search for Insurance Section */}
          <div className='mb-8 px-8'>
            <h3 className='text-lg font-medium text-gray-900 mb-4'>
              Search for your insurance
            </h3>
            <div className='relative'>
              <select
                className='w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent transition-colors appearance-none bg-white'
                style={
                  {
                    '--tw-ring-color':
                      config?.active_theme?.color_scheme?.primary || '#20B2AA',
                  } as React.CSSProperties
                }
                defaultValue=''
              >
                <option value='' disabled>
                  Select
                </option>
                <option value='other'>Other Insurance Provider</option>
                <option value='self-pay'>Self Pay</option>
                <option value='medicare'>Medicare</option>
                <option value='medicaid'>Medicaid</option>
              </select>
              <div className='absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none'>
                <svg
                  className='w-5 h-5 text-gray-400'
                  fill='none'
                  stroke='currentColor'
                  viewBox='0 0 24 24'
                >
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={2}
                    d='M19 9l-7 7-7-7'
                  />
                </svg>
              </div>
            </div>
          </div>

          {/* Next Button */}
          <div className='flex justify-center'>
            <button
              disabled={!selectedInsurance || isNavigating}
              onClick={handleNext}
              className={`h-[54px] min-w-[300px] px-6 py-3 rounded-xl text-base transition-all duration-300 flex items-center justify-center gap-2.5 ${
                selectedInsurance && !isNavigating
                  ? 'text-white shadow-[var(--shadow-xs)] font-semibold hover:opacity-90'
                  : 'bg-[var(--color-gray-100)] text-[var(--color-gray-500)] shadow-[var(--shadow-xs)] font-normal cursor-not-allowed'
              }`}
              style={
                selectedInsurance && !isNavigating
                  ? { backgroundColor: 'var(--clinic-primary)' }
                  : {}
              }
            >
              {isNavigating ? (
                <>
                  <LoadingSpinner size='sm' text='' />
                  Loading...
                </>
              ) : (
                'Next'
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Footer */}
      <Footer mobileColumnLayout={true} />
    </div>
  );
}
